import { ReactNode, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../components/ui/popover";
import { Import, Upload } from "lucide-react";
import { cn } from "../../../../lib/utils";
import { HoverTooltip } from "../../../../components/HoverTooltip";

interface UploadSourcePopoverProps {
  trigger: ReactNode;
  onUploadFromComputer: () => void;
  onImportFromOptro: () => void;
  optroDisabled?: boolean;
  onOpenChange?: (open: boolean) => void;
  // Wraps the trigger only: the menu is portaled, but its focus events still
  // bubble up to this component in React, so wrapping the menu too would
  // reopen the tooltip over it.
  tooltip?: string;
}

export const UploadSourcePopover = ({
  trigger,
  onUploadFromComputer,
  onImportFromOptro,
  optroDisabled,
  onOpenChange,
  tooltip,
}: UploadSourcePopoverProps) => {
  const [open, setOpen] = useState(false);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  const select = (fn: () => void) => () => {
    handleOpenChange(false);
    fn();
  };

  const popoverTrigger = <PopoverTrigger asChild>{trigger}</PopoverTrigger>;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      {tooltip ? (
        <HoverTooltip content={tooltip} suppressed={open}>
          {popoverTrigger}
        </HoverTooltip>
      ) : (
        popoverTrigger
      )}
      <PopoverContent align="start" className="w-80 p-1">
        <SourceItem
          icon={<Upload size={15} />}
          label="Upload from computer"
          onSelect={select(onUploadFromComputer)}
        />
        <SourceItem
          icon={<Import size={15} />}
          label="Import from Optro (Workstream Task)"
          onSelect={select(onImportFromOptro)}
          disabled={optroDisabled}
        />
      </PopoverContent>
    </Popover>
  );
};

interface SourceItemProps {
  icon: ReactNode;
  label: string;
  onSelect: () => void;
  disabled?: boolean;
}

const SourceItem = ({ icon, label, onSelect, disabled }: SourceItemProps) => (
  <button
    type="button"
    onClick={() => {
      if (disabled) return;
      onSelect();
    }}
    disabled={disabled}
    className={cn(
      "w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm text-stone-700 transition-colors text-left",
      disabled
        ? "opacity-50 cursor-not-allowed"
        : "hover:bg-stone-100 hover:text-stone-900 cursor-pointer",
    )}
  >
    <span className="text-stone-500 shrink-0">{icon}</span>
    <span>{label}</span>
  </button>
);
